/**
 * Firebase initialization for authentication and feedback (Firestore).
 * If REACT_APP_FIREBASE_API_KEY is not set, auth will use demo mode (see AuthContext).
 */
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

/** @param {string} name */
function trimReactEnv(name) {
  const v = process.env[name];
  if (v == null) return '';
  return String(v).trim();
}

/** Names expected in `.env` for the Firebase web app (see `.env.example`). */
export const FIREBASE_WEB_APP_ENV_NAMES = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
];

const firebaseConfig = {
  apiKey: trimReactEnv('REACT_APP_FIREBASE_API_KEY'),
  authDomain: trimReactEnv('REACT_APP_FIREBASE_AUTH_DOMAIN'),
  projectId: trimReactEnv('REACT_APP_FIREBASE_PROJECT_ID'),
  storageBucket: trimReactEnv('REACT_APP_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: trimReactEnv('REACT_APP_FIREBASE_MESSAGING_SENDER_ID'),
  appId: trimReactEnv('REACT_APP_FIREBASE_APP_ID'),
};

let app = null;
let auth = null;
let db = null;
/** Firebase Storage - use for large files (e.g. video); upload via Console or SDK. */
let storage = null;

if (firebaseConfig.apiKey) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  if (firebaseConfig.storageBucket) {
    storage = getStorage(app);
  }
}

export { app, auth, db, storage };
export const isFirebaseConfigured = () => !!auth;

/**
 * For setup UI: which web-app env vars are empty in the current build (CRA inlines at compile time).
 * @returns {{ apiKeySet: boolean, missingNames: string[], allSet: boolean }}
 */
export function getFirebaseWebEnvDiagnostics() {
  const missingNames = FIREBASE_WEB_APP_ENV_NAMES.filter((name) => !trimReactEnv(name));
  return {
    apiKeySet: Boolean(trimReactEnv('REACT_APP_FIREBASE_API_KEY')),
    missingNames,
    allSet: missingNames.length === 0,
  };
}
